import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { Home as HomeIcon, Beaker, X as XIcon, Wallet, Sparkles, MapPin } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const ELITE_DISCOUNT_PCT = 5;

const LabBooking = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [cart, setCart] = useState([]);
    const [collectionType, setCollectionType] = useState("home");
    const [addresses, setAddresses] = useState([]);
    const [addressId, setAddressId] = useState("");
    const [walletBal, setWalletBal] = useState(0);
    const [useWallet, setUseWallet] = useState(false);
    // patient details
    const [name, setName] = useState("");
    const [age, setAge] = useState("");
    const [gender, setGender] = useState("male");
    const [phone, setPhone] = useState("");
    // slot
    const [date, setDate] = useState("");
    const [time, setTime] = useState("09:00");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const raw = sessionStorage.getItem("lab-cart");
        const items = raw ? JSON.parse(raw) : [];
        setCart(items);
        if (items.length === 0) {
            toast.message("Your lab cart is empty.");
        }
    }, []);

    useEffect(() => {
        if (!user) return;
        setName(user.full_name || "");
        setPhone(user.phone || "");
        setAddresses(user.addresses || []);
        const def = (user.addresses || []).find((a) => a.is_default) || (user.addresses || [])[0];
        if (def) setAddressId(def.id);
        api.get("/wallet").then((r) => setWalletBal(r.data.balance || 0)).catch(() => {});
        // default date = tomorrow
        const t = new Date();
        t.setDate(t.getDate() + 1);
        setDate(t.toISOString().slice(0, 10));
    }, [user]);

    const removeItem = (key) => {
        const next = cart.filter((x) => x.key !== key);
        setCart(next);
        sessionStorage.setItem("lab-cart", JSON.stringify(next));
    };

    const subtotal = useMemo(() => cart.reduce((s, x) => s + Number(x.price || 0), 0), [cart]);
    const eliteActive = !!(user?.elite?.active);
    const eliteDiscount = eliteActive ? Math.round(subtotal * ELITE_DISCOUNT_PCT) / 100 : 0;
    const total = Math.max(0, subtotal - eliteDiscount);
    const walletApplied = useWallet ? Math.min(walletBal, total) : 0;
    const netPayable = Math.max(0, total - walletApplied);

    const submit = async () => {
        if (cart.length === 0) { toast.error("Cart is empty"); return; }
        if (!name.trim() || !age || !phone.trim()) { toast.error("Fill patient details"); return; }
        if (collectionType === "home" && !addressId) { toast.error("Pick a collection address"); return; }
        if (!date || !time) { toast.error("Pick a collection slot"); return; }
        const scheduled_at = new Date(`${date}T${time}:00`).toISOString();
        if (new Date(scheduled_at) <= new Date()) { toast.error("Slot must be in the future"); return; }

        setSubmitting(true);
        try {
            const payload = {
                items: cart.map((x) => x.test_id ? { test_id: x.test_id } : { package_id: x.package_id }),
                patient_details: { full_name: name.trim(), age: Number(age), gender, phone: phone.trim() },
                collection: { type: collectionType, address_id: collectionType === "home" ? addressId : null, scheduled_at, notes: notes.trim().slice(0, 500) },
                use_wallet: useWallet,
            };
            const { data } = await api.post("/lab-orders", payload);
            sessionStorage.removeItem("lab-cart");
            if (data.amount === 0) {
                toast.success("Lab order confirmed");
                navigate(`/lab-orders/${data.lab_order_id}`);
            } else {
                toast.success("Redirecting to payment…");
                window.location.href = data.redirect_url;
            }
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || d?.code || "Could not book lab order");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthGuard>
            <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
                <Navbar />
                <main className="mx-auto max-w-5xl px-6 py-10 lg:px-12">
                    <Link to="/lab-tests" className="text-xs text-zinc-500 hover:underline">← Back to lab tests</Link>
                    <h1 className="mt-2 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50" style={{ letterSpacing: "-0.01em" }}>Book your lab tests</h1>
                    <p className="mt-1 text-sm text-zinc-500">Tell us where to collect the sample and when.</p>

                    <div className="mt-8 grid gap-6 lg:grid-cols-3">
                        {/* Left: form */}
                        <div className="space-y-6 lg:col-span-2">
                            <section className="rounded-2xl border border-zinc-100 bg-white p-5 dark:bg-white/[0.03] dark:border-white/10" data-testid="lab-booking-patient-card">
                                <p className="label-uppercase text-zinc-500">Patient details</p>
                                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="lab-patient-name" />
                                    <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" className="rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="lab-patient-phone" />
                                    <input type="number" min="0" max="130" value={age} onChange={(e) => setAge(e.target.value)} placeholder="Age" className="rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="lab-patient-age" />
                                    <select value={gender} onChange={(e) => setGender(e.target.value)} className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="lab-patient-gender">
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                            </section>

                            <section className="rounded-2xl border border-zinc-100 bg-white p-5 dark:bg-white/[0.03] dark:border-white/10" data-testid="lab-booking-collection-card">
                                <p className="label-uppercase text-zinc-500">Sample collection</p>
                                <div className="mt-3 flex gap-2" data-testid="lab-booking-collection-type">
                                    <button type="button" onClick={() => setCollectionType("home")} className={`flex flex-1 items-center gap-2 rounded-xl border px-3 py-2 text-sm ${collectionType === "home" ? "border-brand-500 bg-brand-50 text-brand-700" : "border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`} data-testid="lab-collection-home">
                                        <HomeIcon className="h-4 w-4" /> Home pickup
                                    </button>
                                    <button type="button" onClick={() => setCollectionType("lab")} className={`flex flex-1 items-center gap-2 rounded-xl border px-3 py-2 text-sm ${collectionType === "lab" ? "border-brand-500 bg-brand-50 text-brand-700" : "border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`} data-testid="lab-collection-lab">
                                        <Beaker className="h-4 w-4" /> Visit lab
                                    </button>
                                </div>

                                {collectionType === "home" && (
                                    <div className="mt-4">
                                        <p className="text-xs font-medium text-zinc-500">Pick an address</p>
                                        {addresses.length === 0 ? (
                                            <p className="mt-2 rounded-md border border-dashed border-zinc-200 p-3 text-xs text-zinc-500">No saved address. <Link to="/account" className="text-brand-700 hover:underline">Add one →</Link></p>
                                        ) : (
                                            <ul className="mt-2 space-y-2" data-testid="lab-booking-address-list">
                                                {addresses.map((a) => (
                                                    <li key={a.id}>
                                                        <label className={`flex cursor-pointer items-start gap-2 rounded-xl border px-3 py-2 text-sm ${addressId === a.id ? "border-brand-500 bg-brand-50" : "border-zinc-200 hover:bg-zinc-50"}`}>
                                                            <input type="radio" name="addr" checked={addressId === a.id} onChange={() => setAddressId(a.id)} className="mt-1" data-testid={`lab-address-${a.id}`} />
                                                            <span>
                                                                <span className="block font-medium text-zinc-900 dark:text-zinc-50">{a.label || "Address"} {a.is_default && <span className="ml-1 rounded bg-brand-100 px-1 text-[10px] text-brand-700">default</span>}</span>
                                                                <span className="block text-xs text-zinc-500"><MapPin className="mr-0.5 inline h-3 w-3" />{[a.line1, a.line2, a.city, a.state, a.pincode].filter(Boolean).join(", ")}</span>
                                                            </span>
                                                        </label>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}

                                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    <label className="block">
                                        <span className="text-xs font-medium text-zinc-500">Preferred date</span>
                                        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="lab-collection-date" />
                                    </label>
                                    <label className="block">
                                        <span className="text-xs font-medium text-zinc-500">Preferred time</span>
                                        <select value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="lab-collection-time">
                                            {["06:30", "07:30", "08:30", "09:30", "10:30", "11:30", "16:00", "17:00", "18:00"].map((s) => (<option key={s} value={s}>{s}</option>))}
                                        </select>
                                    </label>
                                </div>
                                <textarea rows={2} maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Special instructions (optional)" className="mt-3 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="lab-collection-notes" />
                            </section>
                        </div>

                        {/* Right: summary */}
                        <aside className="space-y-4">
                            <section className="rounded-2xl border border-zinc-100 bg-white p-5 dark:bg-white/[0.03] dark:border-white/10" data-testid="lab-booking-summary">
                                <p className="label-uppercase text-zinc-500">Cart</p>
                                {cart.length === 0 ? (
                                    <p className="mt-2 text-xs text-zinc-500">Empty. <Link to="/lab-tests" className="text-brand-700 hover:underline">Add tests</Link></p>
                                ) : (
                                    <ul className="mt-2 space-y-2">
                                        {cart.map((it) => (
                                            <li key={it.key} className="flex items-start justify-between gap-2 text-sm" data-testid={`lab-cart-item-${it.key}`}>
                                                <span className="min-w-0">
                                                    <span className="block font-medium text-zinc-900 dark:text-zinc-50 line-clamp-1">{it.name}</span>
                                                    <span className="text-[11px] text-zinc-500">{it.test_id ? "Test" : "Package"}</span>
                                                </span>
                                                <span className="flex items-center gap-2">
                                                    <span className="text-sm font-medium text-zinc-900 dark:text-zinc-50">{inr(it.price)}</span>
                                                    <button onClick={() => removeItem(it.key)} className="text-zinc-400 hover:text-rose-500" data-testid={`lab-cart-remove-${it.key}`}><XIcon className="h-3.5 w-3.5" /></button>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <div className="mt-4 space-y-1 border-t border-zinc-100 pt-3 text-xs dark:border-white/10">
                                    <p className="flex justify-between text-zinc-500"><span>Subtotal</span><span>{inr(subtotal)}</span></p>
                                    {eliteActive && (
                                        <p className="flex justify-between text-amber-700" data-testid="lab-summary-elite-discount">
                                            <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" />Elite discount ({ELITE_DISCOUNT_PCT}%)</span>
                                            <span>− {inr(eliteDiscount)}</span>
                                        </p>
                                    )}
                                    {walletApplied > 0 && <p className="flex justify-between text-emerald-700"><span>Wallet</span><span>− {inr(walletApplied)}</span></p>}
                                    <p className="flex justify-between border-t border-zinc-100 pt-2 text-sm font-semibold text-zinc-900 dark:border-white/10 dark:text-zinc-50" data-testid="lab-summary-total"><span>Total</span><span>{inr(netPayable)}</span></p>
                                </div>
                                {walletBal > 0 && (
                                    <label className="mt-3 flex items-center gap-2 text-xs text-zinc-700 dark:text-zinc-300">
                                        <input type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} data-testid="lab-use-wallet" />
                                        <Wallet className="h-3 w-3" /> Use wallet balance ({inr(walletBal)})
                                    </label>
                                )}
                                <button onClick={submit} disabled={submitting || cart.length === 0} className="btn-brand mt-4 w-full disabled:opacity-50" data-testid="lab-booking-pay-btn">
                                    {submitting ? "Processing…" : netPayable === 0 ? "Confirm booking" : `Pay ${inr(netPayable)}`}
                                </button>
                                <p className="mt-2 text-[10px] text-zinc-500">Payments are processed via Zoho Payments (PCI-DSS).</p>
                            </section>
                        </aside>
                    </div>
                </main>
                <Footer />
            </div>
        </AuthGuard>
    );
};

export default LabBooking;
